import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDrawerNavKeys } from '../common/DrawerNavRail';
import DrawerLoadingFallback from '../common/DrawerLoadingFallback';
import { sid } from '../../utils/shortId';
import { registerEntityOpener } from '../../utils/openEntity';
import { noProviderOpener } from '../../utils/drawerNoProvider';
import { providerIfModuleOn } from '../../lib/hostModules';

/**
 * createRecordDrawer — factory dựng context drawer URL-driven cho 1 loại bản ghi CRM
 * (lead / liên hệ / công ty). Trước đây 3 file context copy-paste y hệt nhau; gom về đây
 * để thêm điều hướng trước/sau một lần là cả 3 cùng có.
 *
 * Nguồn sự thật của "đang mở cái nào" = query param (vd `?leadId=<short>`), nên link dán
 * vào chat/tab khác vẫn mở đúng popup. Route full-page `/crm/leads/:id` GIỮ NGUYÊN — đây
 * là 2 đường vào song song, không thay thế nhau.
 *
 * Điều hướng trước/sau (nút ▲▼ bên trái popup) chạy trên "danh sách đã đăng ký": trang
 * danh sách gọi useDrawerNav() để báo thứ tự dòng đang hiển thị + trang hiện tại. Không
 * nhét danh sách vào URL (quá dài) ⇒ mở bằng deep-link thì không có nút ▲▼ (đúng: lúc đó
 * không tồn tại "bản ghi kế tiếp" nào cả).
 *
 * @param {string}   param  tên query param, cũng là tên prop id truyền cho Drawer
 * @param {string}   kind   tên loại trong kernel utils/openEntity (để link/card bấm ra POPUP
 *                          thay vì nhảy trang chi tiết — xem ENTITY_LINK_RULES)
 * @param {Function} Drawer component drawer (nên bọc lazyWithReload ở call-site)
 * @param {object}   names  { open, close, openId } — tên API giữ nguyên cho consumer cũ
 */
export default function createRecordDrawer({ param, kind, Drawer, names }) {
  const Ctx = createContext(null);

  // Provider không nằm trên cây React của người gọi (hay gặp nhất: nội dung xem TRONG một
  // drawer KHÁC) → lui về sổ đăng ký module-scope. Xem utils/drawerNoProvider.
  const NO_PROVIDER = {
    [names.open]: noProviderOpener(kind),
    [names.close]: () => {},
    [names.openId]: null,
    version: 0,
    bumpVersion: () => {},
    registerNav: () => {},
    navInfo: null,
    navGo: () => {},
  };

  function Provider({ children }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const id = searchParams.get(param);
    const [version, setVersion] = useState(0);
    const [nav, setNav] = useState(null);
    // Đơn xin "nhảy trang rồi mở đầu/cuối trang mới" — chờ danh sách mới về mới giải quyết.
    const pendingEdge = useRef(null);

    const open = useCallback((rawId) => {
      if (!rawId) return;
      const short = sid(rawId);
      setSearchParams(prev => {
        if (prev.get(param) === short) return prev;
        const next = new URLSearchParams(prev);
        next.set(param, short);
        return next;
      }, { replace: true });
    }, [setSearchParams]);

    const close = useCallback(() => {
      pendingEdge.current = null;
      setSearchParams(prev => {
        if (!prev.get(param)) return prev;
        const next = new URLSearchParams(prev);
        next.delete(param);
        return next;
      }, { replace: true });
    }, [setSearchParams]);

    const bumpVersion = useCallback(() => setVersion(v => v + 1), []);
    const registerNav = useCallback((n) => setNav(n), []);

    // Khai báo với kernel: "loại này mở bằng popup" → mọi link/card trỏ tới bản ghi loại này
    // bung popup tại chỗ thay vì nhảy sang trang chi tiết (trắng trang chờ chunk).
    useEffect(() => registerEntityOpener(kind, open), [open]);

    // Vị trí hiện tại trong danh sách + còn trước/sau hay không (tính cả sang trang).
    const navInfo = useMemo(() => {
      if (!id || !nav?.ids?.length) return null;
      const index = nav.ids.indexOf(id);
      if (index === -1) return null;
      const page = nav.page || 1;
      const totalPages = nav.totalPages || 1;
      const pageSize = nav.pageSize || nav.ids.length;
      return {
        index,
        position: (page - 1) * pageSize + index + 1,
        total: nav.total || nav.ids.length,
        hasPrev: index > 0 || page > 1,
        hasNext: index < nav.ids.length - 1 || page < totalPages,
        loading: !!nav.loading,
      };
    }, [id, nav]);

    const navGo = useCallback((delta) => {
      if (!id || !nav?.ids?.length) return;
      const index = nav.ids.indexOf(id);
      if (index === -1) return;
      const target = index + delta;
      if (target >= 0 && target < nav.ids.length) { open(nav.ids[target]); return; }
      // Chạm mép trang → xin trang kế rồi mở phần tử đầu/cuối của trang đó.
      const page = nav.page || 1;
      const totalPages = nav.totalPages || 1;
      if (delta > 0 && page < totalPages) {
        pendingEdge.current = { want: 'first', fromKey: nav.ids.join(',') };
        nav.onPageChange?.(page + 1);
      } else if (delta < 0 && page > 1) {
        pendingEdge.current = { want: 'last', fromKey: nav.ids.join(',') };
        nav.onPageChange?.(page - 1);
      }
    }, [id, nav, open]);

    // Giải quyết đơn nhảy trang. Điều kiện `fromKey` khác + hết loading là bắt buộc:
    // setPage() đổi nav.page NGAY nhưng ids thì chờ fetch xong mới đổi ⇒ không chốt sớm
    // kẻo mở lại đúng phần tử đầu của trang CŨ.
    const navIdsKey = nav?.ids?.join(',');
    const navLoading = nav?.loading;
    useEffect(() => {
      const pending = pendingEdge.current;
      if (!pending || navLoading || !navIdsKey) return;
      if (navIdsKey === pending.fromKey) return;
      pendingEdge.current = null;
      const ids = navIdsKey.split(',');
      open(pending.want === 'first' ? ids[0] : ids[ids.length - 1]);
    }, [navIdsKey, navLoading, open]);

    // ↑/↓ trần (Alt + ↑/↓ vẫn chạy) — hook lo phần nhường phím cho ô đang gõ/dropdown.
    useDrawerNavKeys(navGo, !!id);

    const value = useMemo(() => ({
      [names.open]: open,
      [names.close]: close,
      [names.openId]: id,
      version,
      bumpVersion,
      registerNav,
      navInfo,
      navGo,
    }), [open, close, id, version, bumpVersion, registerNav, navInfo, navGo]);

    return (
      <Ctx.Provider value={value}>
        {children}
        {id && (
          <Suspense fallback={<DrawerLoadingFallback onClose={close} />}>
            <Drawer
              open={!!id}
              {...{ [param]: id }}
              onClose={close}
              navInfo={navInfo}
              onNavGo={navGo}
            />
          </Suspense>
        )}
      </Ctx.Provider>
    );
  }

  function useDrawer() {
    return useContext(Ctx) || NO_PROVIDER;
  }

  /**
   * useDrawerNav — trang danh sách khai báo thứ tự dòng để popup có nút trước/sau.
   * Gọi ở list page, ngay cạnh state phân trang. Tự huỷ đăng ký khi rời trang.
   */
  function useDrawerNav({ items, page, totalPages, total, pageSize, loading, onPageChange }) {
    const { registerNav } = useDrawer();
    const idsKey = useMemo(
      () => (items || []).map(x => sid(x?.id)).filter(Boolean).join(','),
      [items]
    );

    useEffect(() => {
      registerNav({
        ids: idsKey ? idsKey.split(',') : [],
        page, totalPages, total, pageSize, loading, onPageChange,
      });
    }, [idsKey, page, totalPages, total, pageSize, loading, onPageChange, registerNav]);

    useEffect(() => () => registerNav(null), [registerNav]);
  }

  // Cụm CRM tắt ở host (`modules.crm = false` — Site Connect 12/10/2026) ⇒ Provider rỗng, xem lib/hostModules.
  return { Provider: providerIfModuleOn('crm', Provider), useDrawer, useDrawerNav };
}
